import { FieldHelpTooltip } from './FieldHelpTooltip';

interface FieldLabelRowProps {
  label: string;
  tooltip?: string;
}

/**
 * A visible label + field-level help icon, rendered as a sibling row above a control instead of
 * via that control's own `fieldLevelHelpTooltip` prop. `@salesforce/design-system-react`'s
 * `Input`/`Combobox` both implement `fieldLevelHelpTooltip` through a private
 * `FieldLevelHelpTooltip` wrapper that reads `fieldLevelHelpTooltip.props` and re-spreads them
 * onto a *fresh* `Tooltip` it renders itself (see `tooltip/private/field-level-help-tooltip`) —
 * it expects the literal `<Tooltip>` element shape, not a custom component that merely renders
 * one. Passing `<FieldHelpTooltip label=.../>` there silently drops the actual trigger button,
 * which is what rendered as a dead, unclickable-looking icon.
 *
 * `aria-hidden` here since this is a purely visual label — the control it labels keeps rendering
 * its own real, programmatically-associated `<label>` via `assistiveText.label` (same text),
 * just visually hidden, so this is still a single accessible label from a screen reader's
 * perspective, not two competing ones.
 */
export const FieldLabelRow: React.FC<FieldLabelRowProps> = ({ label, tooltip }) => (
  <div
    aria-hidden="true"
    className="slds-grid slds-grid_vertical-align-center"
    style={{
      gap: 'var(--slds-g-spacing-1, 0.25rem)',
      marginBottom: 'var(--slds-g-spacing-1)',
    }}
  >
    <span className="slds-form-element__label" style={{ marginBottom: 0 }}>
      {label}
    </span>
    {tooltip ? <FieldHelpTooltip label={tooltip} /> : null}
  </div>
);
